/* =============================================================================
   VIEWS / CUSTOMER-FORM — Reservierungs-Formular für anonyme Gäste
   =============================================================================
   Enthält:
     - initCustomerForm (Slots + Default-Werte + Live-Preview-Hooks)
     - refreshAvailabilityPreview (debounced)
     - renderAvailabilityPreview (Status-Box + Alternativen)
     - applyAlternativeTime (Klick-Handler für Vorschläge)
     - togglePreorder / gatherPreorderItems (delegiert an menu-picker.js)
     - submitReservation (Customer-Submit über SECURITY-DEFINER-RPC)
     - resetCustomerForm

   State + globaler `currentRestaurantId` werden lazy via `window.*` aufgelöst.
============================================================================ */

import { htmlEscape, fmtEUR, todayISO, showToast, detectDstTransition } from '../utils/helpers.js';
import {
  generateTimeSlots,
  evaluateReservation
} from '../core/reservation-engine.js';
import {
  previewAvailability,
  suggestAlternativeTimes,
  getCurrentSlotDuration
} from '../core/capacity.js';
import {
  initMenuPicker,
  getOrder as getMenuOrder,
  resetMenuPicker,
  validateOrder as validateMenuOrder
} from './menu-picker.js';
// S204 — Gast-Wunschtisch-Plan (ersetzt das #r-area-Dropdown).
import {
  mountCustomerPlan,
  refreshCustomerPlanBusy,
  getWishTableId,
  resetCustomerPlan
} from './customer-plan.js';
// S266 — Gast-Kanäle einzeln schaltbar (phase_196): welche Einstiege die
// Gastseite zeigt, entscheidet settings.guestChannels. Single Source in core/.
import {
  guestChannelEnabled,
  activeGuestEntryChannels,
  serviceTypeForChannel,
  channelForServiceType,
  guestEntryLabel,
  guestChannelDisabledMessage,
  channelDisabledKeyFromError
} from '../core/guest-channels.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { reservations: [], tables: [], menu: [], settings: {} };
}
// Service-Type: 'dine-in' (Standard, Tisch-Reservierung) oder 'takeaway'
// (Vorbestellung zur Abholung — keine Tisch-Zuweisung, kein Kapazitäts-Check).
// Wird über die zwei Toggle-Chips oben im Formular gesteuert.
// S266: Default = erster AKTIVER Kanal (applyGuestChannels setzt ihn beim Init).
let _serviceType = 'dine-in';
export function getServiceType() { return _serviceType; }

/** S266 — Service-Typ des ersten aktiven Einstiegs (Fallback 'dine-in'). */
function _defaultServiceType(settings) {
  const first = activeGuestEntryChannels(settings || {})[0];
  return serviceTypeForChannel(first) || 'dine-in';
}

function currentRestaurantIdLazy() {
  // Zentraler Resolver (F-04). Fällt auf die globale window.currentRestaurantId
  // (aus state.js) bzw. die Config-Default-ID zurück, falls der Storage-Adapter
  // ausnahmsweise noch nicht initialisiert ist.
  if (window.App && typeof window.App.getCurrentRestaurantId === 'function') {
    return window.App.getCurrentRestaurantId();
  }
  if (typeof window.currentRestaurantId === 'function') return window.currentRestaurantId();
  return (window.AppConfig && window.AppConfig.defaultRestaurantId) || 'rest_default';
}

// -----------------------------------------------------------------------------
// Initialisierung
// -----------------------------------------------------------------------------
export function initCustomerForm() {
  const state = getState();
  if (!state || !state.settings) {
    console.warn('[initCustomerForm] State noch nicht initialisiert, überspringe Render.');
    return;
  }
  const sel = document.getElementById('r-time');
  if (sel) {
    sel.innerHTML = '';
    generateTimeSlots().forEach(t => {
      const o = document.createElement('option');
      o.value = t;
      o.textContent = t + ' Uhr';
      sel.appendChild(o);
    });
    if ([...sel.options].some(o => o.value === '19:00')) sel.value = '19:00';
  }
  const d = document.getElementById('r-date');
  if (d) { d.min = todayISO(); d.value = todayISO(); }

  // S48 Multi-Tenant-Branding-Fix: Brand-Name-Setter werden zentral von
  // applyBrand() in core/branding.js übernommen (über auth:state-changed-
  // Listener in bootstrap.js + initialen bootstrap-Aufruf). Hier nicht mehr
  // lokal setzen — sonst hätten wir wieder zwei Wahrheits-Quellen für den
  // Brand-Header. initCustomerForm kann mit Pilot-State vor Tenant-Auth
  // laufen; der zentrale Aufruf greift sowieso nach _initFromSession + bei
  // jedem Tenant-Wechsel. Memory `feedback_gg_customer_form_dual` analog.
  const mt = document.getElementById('manual-threshold-display');
  if (mt) mt.textContent = state.settings.manualConfirmThreshold;
  const oh = document.getElementById('outdoor-hint');
  if (oh) oh.style.display = state.settings.outdoorEnabled ? 'none' : 'block';

  const gdprField = document.getElementById('gdpr-field');
  if (gdprField) {
    gdprField.style.display = state.settings.requireGdprConsent ? 'block' : 'none';
    const link = document.getElementById('gdpr-policy-link');
    if (link) {
      link.innerHTML = state.settings.gdprPolicyUrl
        ? `<a href="${htmlEscape(state.settings.gdprPolicyUrl)}" target="_blank" rel="noopener">Mehr erfahren</a>`
        : '';
    }
  }
  // S204 — Bereich/Wunschtisch: das Dropdown ist durch das Plan-Widget ersetzt.
  // Verfügbare Bereiche (outdoor/club) regelt der Plan selbst (enabledPlanAreas).
  mountCustomerPlan();
  initMenuPicker();
  // S266 — Gast-Kanäle anwenden (Chips, Formular, Vorbestellung, Hero) und
  // den Service-Typ auf den ersten aktiven Kanal setzen. Kein Auto-Öffnen
  // eines Modals beim Init — das erledigt erst der Chip-Klick.
  applyGuestChannels(state.settings);
  ['r-date', 'r-time', 'r-guests', 'r-area'].forEach(id => {
    const el = document.getElementById(id);
    if (el && !el._hasAvailabilityHook) {
      el.addEventListener('input',  refreshAvailabilityPreview);
      el.addEventListener('change', refreshAvailabilityPreview);
      el._hasAvailabilityHook = true;
    }
  });
  refreshAvailabilityPreview();
}

// -----------------------------------------------------------------------------
// Live-Verfügbarkeits-Preview
// -----------------------------------------------------------------------------
let _availabilityDebounce = null;
let _availabilitySeq = 0;
export function refreshAvailabilityPreview() {
  clearTimeout(_availabilityDebounce);
  _availabilityDebounce = setTimeout(() => { _runAvailabilityPreview(); }, 80);
}

/**
 * S272-A — ein Lauf der Verfügbarkeits-Preview: ERST den Cache auffrischen
 * (`refreshAvailability()`: get_day_load-Stubs + aktive Belegungen; die 30-s-TTL
 * in storage.js begrenzt die Last, dazwischen kommt der Aufruf aus dem Cache
 * zurück), DANN rendern. Vorher wurde refreshAvailability nur beim Boot und nach
 * Auth-Wechsel aufgerufen — ein Gast mit offener Seite sah dauerhaft den Stand
 * vom Seitenaufruf (S271-Retest 4.2: nach Walk-In-Ende blieb es bei 43).
 * Race-sicher über eine Sequenznummer: nur der jüngste Lauf rendert. Ein Fehler
 * beim Auffrischen ist nur Info — gerendert wird dann mit dem letzten Stand.
 * Liefert ein Promise (für Tests); Aufrufer warten nicht darauf.
 */
export async function _runAvailabilityPreview() {
  const seq = ++_availabilitySeq;
  const refresh = window.App?.storage?.reservations?.refreshAvailability;
  if (typeof refresh === 'function') {
    try {
      await refresh.call(window.App.storage.reservations);
    } catch (e) {
      console.info('[customer-form] refreshAvailability übersprungen —', e && e.message);
    }
  }
  if (seq !== _availabilitySeq) return false;  // jüngerer Lauf übernimmt
  const date = document.getElementById('r-date')?.value;
  const time = document.getElementById('r-time')?.value;
  const guests = parseInt(document.getElementById('r-guests')?.value, 10) || 0;
  const area = document.getElementById('r-area')?.value || 'any';
  renderAvailabilityPreview(date, time, guests, area);
  updateDstHint(date);
  // S204 — Wunschtisch-Plan mitziehen (Belegt-Set + Kachel-Status neu; eigener
  // Debounce, race-sicher). Datum/Zeit/Gäste ändern die frei/belegt/zu-klein-Sicht.
  refreshCustomerPlanBusy();
  return true;
}

/** S272-B — Prüf-Optionen des Gastformulars: die Standard-Reservierungsdauer
 *  (settings.slotDurationMin), mit der die Anfrage auch abgeschickt wird. */
export function guestPreviewOpts() {
  return { durationMin: getCurrentSlotDuration() };
}

/** Phase 28 (S26): DST-Hinweis am Übergangs-Tag.
 *  Wir blockieren die Doppelstunde NICHT (Restaurant hat zu der Zeit eh
 *  geschlossen — Default-Opening 11:00-23:00), markieren sie aber visuell
 *  mit einem Hinweis, damit bei verschobenen Öffnungszeiten kein Schaden
 *  entsteht. Frühling: 02:00-02:59 fehlt. Herbst: 02:00-02:59 doppelt. */
/** Pure-Helper für Test-Coverage (S38): liefert den DST-Hint-Text für
 *  einen detectDstTransition()-Output. Liefert '' wenn kein Übergangstag.
 *  Reine String-Logik, kein DOM. */
function _buildDstHintText(dst) {
  if (!dst) return '';
  if (dst.type === 'spring') {
    return '⚠ Zeitumstellung: An diesem Tag wird die Uhr um 02:00 auf 03:00 vorgestellt — die Stunde 02:00–02:59 existiert nicht.';
  }
  return '⚠ Zeitumstellung: An diesem Tag wird die Uhr um 03:00 auf 02:00 zurückgestellt — die Stunde 02:00–02:59 läuft doppelt.';
}

function updateDstHint(date) {
  const hint = document.getElementById('r-dst-hint');
  if (!hint) return;
  if (!date) { hint.style.display = 'none'; hint.textContent = ''; return; }
  const dst = detectDstTransition(date);
  const text = _buildDstHintText(dst);
  if (!text) { hint.style.display = 'none'; hint.textContent = ''; return; }
  hint.textContent = text;
  hint.style.display = 'block';
}

/** Pure-Helper für Test-Coverage (S38): mappt previewAvailability()-Output
 *  auf {icon, msg, sub, className} für die Status-Box. Reine String-/Branch-
 *  Logic, kein DOM. Liefert null bei pa=null/undefined. */
function _buildAvailabilityPreviewContent(pa) {
  if (!pa) return null;
  let icon, msg, sub;
  if (pa.status === 'closed') {
    icon = '🚫';
    msg = pa.reason || 'Nicht buchbar';
    sub = '';
  } else if (pa.status === 'full') {
    icon = '⚠️';
    msg = 'Diese Zeit ist ausgebucht';
    sub = pa.reason || `Aktuell ${pa.bookedSeats} von ${pa.totalCapacity - pa.reservedSeats} verfügbaren Plätzen belegt.`;
  } else if (pa.status === 'tight') {
    icon = '⏳';
    msg = `Eng — noch ${pa.seatsAvailable} Plätze frei`;
    sub = 'Wir freuen uns auf eure Anfrage, sicher ist sicher gleich abschicken.';
  } else {
    icon = '✅';
    msg = `Verfügbar — noch ${pa.seatsAvailable} von ${pa.totalCapacity - pa.reservedSeats} Plätzen frei`;
    sub = pa.assignment
      ? `Wir würden ${pa.assignment.tables.length === 1 ? 'einen Tisch' : pa.assignment.tables.length + ' Tische'} vorschlagen${(pa.assignment.chairsUsed || 0) > 0 ? ` (+ ${pa.assignment.chairsUsed} Zusatzstuhl${pa.assignment.chairsUsed > 1 ? 'e' : ''})` : ''}.`
      : '';
  }
  return { icon, msg, sub, className: 'availability-preview status-' + pa.status };
}

export function renderAvailabilityPreview(date, time, guests, area) {
  const box = document.getElementById('availability-preview');
  const alt = document.getElementById('availability-alternatives');
  if (!box || !alt) return;
  if (!date || !time || !guests) {
    box.style.display = 'none';
    alt.style.display = 'none';
    return;
  }
  // S272-B: explizit mit der Standard-Reservierungsdauer prüfen — genau die
  // Dauer, die submitReservation() abschickt (durationMin: getCurrentSlotDuration()).
  const pa = previewAvailability(date, time, guests, area, guestPreviewOpts());
  const content = _buildAvailabilityPreviewContent(pa);
  box.className = content.className;
  box.innerHTML = `
    <span class="ap-icon">${content.icon}</span>
    <div>
      <div class="ap-msg">${htmlEscape(content.msg)}</div>
      ${content.sub ? `<div class="ap-sub">${htmlEscape(content.sub)}</div>` : ''}
    </div>
  `;
  box.style.display = 'flex';

  if (!pa.canBook && pa.status !== 'closed') {
    const alts = suggestAlternativeTimes(date, time, guests, area, undefined, guestPreviewOpts());
    if (alts.length) {
      alt.innerHTML = `
        <div class="ap-alternatives-head">Diese Zeiten klappen heute noch:</div>
        <div class="ap-alternatives-grid">
          ${alts.map(a => `
            <button type="button" class="ap-alt"
                    onclick="applyAlternativeTime('${a.time}')"
                    title="${a.seatsAvailable} Plätze frei">
              <span class="ap-alt-time">${a.time}</span>
              <span class="ap-alt-seats">${a.seatsAvailable} frei</span>
            </button>
          `).join('')}
        </div>
      `;
      alt.style.display = 'block';
    } else {
      alt.innerHTML = `
        <div class="ap-alternatives-empty">An diesem Tag ist leider keine andere Zeit verfügbar. Probier ein anderes Datum.</div>
      `;
      alt.style.display = 'block';
    }
  } else {
    alt.style.display = 'none';
  }
}

export function applyAlternativeTime(time) {
  const el = document.getElementById('r-time');
  if (el) {
    el.value = time;
    refreshAvailabilityPreview();
    el.focus();
  }
}

// -----------------------------------------------------------------------------
// Vorbestellung (v0.19.0: digitale Ankreuz-Speisekarte, siehe menu-picker.js)
// -----------------------------------------------------------------------------
export function togglePreorder() {
  const chk = document.getElementById('r-preorder-toggle');
  const sec = document.getElementById('preorder-section');
  if (sec && chk) {
    sec.style.display = chk.checked ? 'block' : 'none';
    if (chk.checked) initMenuPicker();
  }
}

/** Sammelt die Vorbestellung im neuen v2-Format aus dem menu-picker.
 *  Liefert das komplette Order-Objekt (mit Items, Sonderwünschen, Summe)
 *  oder `null`, wenn der User keine Vorbestellung aktiviert hat oder
 *  noch kein Item gewählt ist. */
export function gatherPreorderItems() {
  const chk = document.getElementById('r-preorder-toggle');
  if (!chk || !chk.checked) return null;
  const order = getMenuOrder();
  if (!order || !Array.isArray(order.items) || order.items.length === 0) return null;
  return order;
}

// -----------------------------------------------------------------------------
// Submit
// -----------------------------------------------------------------------------

// S29 KRITISCH-Fix: Submit-Lock gegen Doppel-/Mehrfach-Klick.
// Jonas-Funktionstest: 4× Reservierungs-Eingangsbestätigungs-Mail entstand,
// weil der Customer den Submit-Button mehrfach geklickt hat bevor der erste
// Insert durch war → mehrere Reservierungen mit verschiedenen IDs → je eine
// Mail. Lock ist Modul-lokal, das reicht: ein Customer-Form pro Tab.
let _submittingReservation = false;

export async function submitReservation(e) {
  if (e && e.preventDefault) e.preventDefault();

  if (_submittingReservation) {
    console.info('[submitReservation] bereits in Flight — Doppelklick ignoriert.');
    return;
  }
  _submittingReservation = true;
  const submitBtn = (e && e.target && e.target.querySelector)
    ? e.target.querySelector('button[type="submit"]')
    : document.querySelector('#reservation-form button[type="submit"]');
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.dataset._origText = submitBtn.textContent;
    submitBtn.textContent = 'Sende…';
  }

  try {
    return await _submitReservationInner(e);
  } finally {
    _submittingReservation = false;
    if (submitBtn) {
      submitBtn.disabled = false;
      if (submitBtn.dataset._origText) {
        submitBtn.textContent = submitBtn.dataset._origText;
        delete submitBtn.dataset._origText;
      }
    }
  }
}

async function _submitReservationInner(e) {
  const state = getState();

  if (state.settings.requireGdprConsent) {
    const consent = document.getElementById('r-gdpr');
    if (!consent || !consent.checked) {
      showToast('Bitte stimme den Datenschutzbestimmungen zu', 'error');
      return;
    }
  }

  // Vorbestellung validieren: alle Bowl-Patty/Dressing-Pflichtfelder müssen
  // gesetzt sein, Burger müssen ein Brötchen haben.
  const preorderToggle = document.getElementById('r-preorder-toggle');
  if (preorderToggle && preorderToggle.checked) {
    const vr = validateMenuOrder();
    if (!vr.ok) {
      showToast(vr.errors[0] || 'Bitte vervollständige die Vorbestellung.', 'error');
      return;
    }
  }

  // Dieser Submit-Pfad ist ausschließlich für Tisch-Reservierungen (Dine-In).
  // Takeaway-Bestellungen laufen über src/views/takeaway-form.js und nie hier
  // durch — beim Toggle-Wechsel öffnet sich das eigene Bestell-Modal.
  const req = {
    restaurantId: currentRestaurantIdLazy(),
    serviceType: 'dine-in',
    name:   document.getElementById('r-name').value.trim(),
    phone:  document.getElementById('r-phone').value.trim(),
    email:  document.getElementById('r-email').value.trim(),
    date:   document.getElementById('r-date').value,
    time:   document.getElementById('r-time').value,
    guests: parseInt(document.getElementById('r-guests').value, 10),
    area:   document.getElementById('r-area').value,
    notes:  document.getElementById('r-notes').value.trim(),
    preorder: gatherPreorderItems(),
    status: 'pending',
    source: 'online',
    requiresManualConfirm: false,
    assignedTables: [],
    durationMin: getCurrentSlotDuration(),
    // S204 — unverbindlicher Wunschtisch aus dem Plan-Widget (Server validiert +
    // verwirft ungültige still; die verbindliche Zuweisung bleibt beim Annehmen).
    wishTableId: getWishTableId(),
    gdprConsentAt: state.settings.requireGdprConsent ? new Date().toISOString() : null
  };

  if (req.date < todayISO()) {
    showToast('Datum liegt in der Vergangenheit', 'error');
    return;
  }

  const evalResult = evaluateReservation(req);
  const outcome = _buildReservationSubmitOutcome(evalResult, state.settings);
  // Outcome auf req anwenden — semantisch identisch zur vorigen Inline-Logik:
  // im !ok-Branch bleibt assignedTables=[], in ok-Branches kommen die Werte
  // aus evalResult.assignment.
  req.status = outcome.status;
  req.requiresManualConfirm = outcome.requiresManualConfirm;
  if (outcome.declineHint !== null) req.declineHint = outcome.declineHint;
  req.assignedTables  = outcome.assignedTables;
  if (outcome.hasAssignment) {
    req.tablesCombined  = outcome.tablesCombined;
    req.needsExtraChair = outcome.needsExtraChair;
    req.extraChairsUsed = outcome.extraChairsUsed;
  }
  const successMsg = outcome.successMsg;
  const onWaitlist = outcome.onWaitlist;

  let created;
  try {
    created = await window.App.storage.reservations.createCustomer(req);
  } catch (err) {
    console.error('[submitReservation] createCustomer:', err);
    // C-01 Race-Condition: RPC liefert errcode 'GG001', wenn ein Tisch
    // zwischenzeitlich (zwischen Frontend-Capacity-Check und INSERT) durch
    // einen parallelen Submit blockiert wurde. Wir refreshen die
    // Verfuegbarkeit + zeigen alternative Zeitslots, statt nur einen Toast.
    const code = _extractRaceErrorCode(err);
    if (code === 'GG001') {
      try {
        if (window.App.storage && window.App.storage.reservations.refreshAvailability) {
          await window.App.storage.reservations.refreshAvailability({ force: true });
        }
      } catch (e) {
        console.warn('[submitReservation] refresh after GG001:', e);
      }
      const alts = (typeof suggestAlternativeTimes === 'function')
        ? suggestAlternativeTimes(req.date, req.time, req.guests, req.area || 'any', undefined, guestPreviewOpts())
        : [];
      const altText = _buildGG001AlternativeText(alts);
      showToast(
        'Tisch wurde gerade von jemand anderem gebucht. Bitte wähle eine andere Zeit oder reduziere die Personenzahl.' + altText,
        'error',
        9000
      );
      // Verfuegbarkeits-Preview neu rendern, damit der User die aktualisierten Slots sieht.
      try {
        const dateEl  = document.getElementById('r-date');
        const timeEl  = document.getElementById('r-time');
        const gEl     = document.getElementById('r-guests');
        if (dateEl && timeEl && gEl) {
          const pa = previewAvailability(dateEl.value, timeEl.value, Number(gEl.value), req.area || 'any', guestPreviewOpts());
          if (pa && !pa.canBook) {
            // Hinweis-Box fuer Tagesauslastung wird durch das normale
            // updateAvailability() neu gerendert; hier reicht der Toast.
          }
        }
      } catch (_) { /* preview ist optional */ }
      return;
    }
    // S266 — Kanal wurde geschlossen, während die Gastseite offen war: ein
    // Satz statt Postgres-Text, danach Settings neu laden + Einstieg neu bauen.
    const channelKey = channelDisabledKeyFromError(err);
    if (channelKey) {
      showToast(guestChannelDisabledMessage(channelKey), 'error', 8000);
      refreshGuestChannels().catch(e => console.warn('[submitReservation] refreshGuestChannels:', e));
      return;
    }
    showToast('Reservierung konnte nicht angelegt werden: ' + (err.message || err), 'error');
    return;
  }

  if (window.App.storage && window.App.storage.reservations.refreshAvailability) {
    window.App.storage.reservations.refreshAvailability({ force: true })
      .catch(e => console.warn('[submitReservation] refreshAvailability:', e));
  }

  if (window.App.events) {
    // S132c — `waitlist` abgeschafft: immer reservation:created (kein waitlist:joined mehr).
    await window.App.events.emit('reservation:created', created);
  }

  // S226 — Newsletter-Abo (Double-Opt-in) NICHT-FATAL nach erfolgreicher
  // Reservierung: nur bei gesetztem Häkchen, Fehler blockieren die Reservierung nie.
  if (typeof window.maybeSubscribeFromCheckbox === 'function') {
    window.maybeSubscribeFromCheckbox('r-newsletter', req.restaurantId, req.email, 'web_reservation');
  }

  document.getElementById('customer-form-wrapper').style.display = 'none';
  document.getElementById('customer-success').style.display = 'block';
  document.getElementById('success-message').textContent = successMsg;

  // S74 Konzept-Cut: Confirmation-Screen ersetzt die received-Mail.
  // Befüllen mit Reservation-Details + Cancel-Link, damit kein Mail-Backup
  // mehr nötig ist (vorher stand der Cancel-Token nur in der received-Mail).
  try {
    const detailsBox = document.getElementById('customer-success-details');
    const dateSpan   = document.getElementById('success-detail-date');
    const timeSpan   = document.getElementById('success-detail-time');
    const guestSpan  = document.getElementById('success-detail-guests');
    const serviceRow = document.getElementById('success-detail-service-row');
    const serviceSpan= document.getElementById('success-detail-service');
    const hint       = document.getElementById('success-confirmation-hint');
    const linkWrap   = document.getElementById('success-cancel-link-wrap');
    const linkEl     = document.getElementById('success-cancel-link');

    if (detailsBox && dateSpan && timeSpan && guestSpan) {
      const d = created?.date || req.date;
      const t = created?.time || req.time;
      const g = created?.guests ?? req.guests;
      const svc = created?.serviceType || req.serviceType;
      try {
        const dt = new Date(`${d}T${t}`);
        dateSpan.textContent = dt.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
        timeSpan.textContent = dt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
      } catch (_) {
        dateSpan.textContent = d || '—';
        timeSpan.textContent = (typeof t === 'string') ? t.slice(0,5) : '—';
      }
      guestSpan.textContent = String(g ?? '—');
      if (svc && svc !== 'dine-in' && serviceRow && serviceSpan) {
        serviceSpan.textContent = (svc === 'takeaway') ? 'Zum Mitnehmen' : 'Lieferung';
        serviceRow.style.display = '';
      }
      detailsBox.style.display = '';
    }

    if (hint) hint.style.display = '';

    const tok = created?.cancelToken || created?.cancel_token;
    if (linkWrap && linkEl && tok) {
      const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
      const href = `${origin}/index.html#cancel=${tok}`;
      linkEl.href = href;
      linkEl.textContent = href;
      linkWrap.style.display = '';
    }
  } catch (e) {
    console.warn('[submitReservation] Confirmation-Screen-Details konnten nicht gefüllt werden:', e);
  }
}

export function resetCustomerForm() {
  const form = document.getElementById('reservation-form');
  if (form) form.reset();
  resetMenuPicker();
  resetCustomerPlan();   // S204 — Wunschtisch/Segment-Modulstate zurücksetzen
  togglePreorder();
  _serviceType = _defaultServiceType(getState().settings);
  const w = document.getElementById('customer-form-wrapper');
  const s = document.getElementById('customer-success');
  if (w) w.style.display = 'block';
  if (s) s.style.display = 'none';
  // S74 Konzept-Cut: Details-Box + Cancel-Link für nächsten Submit zurücksetzen
  const detailsBox = document.getElementById('customer-success-details');
  const serviceRow = document.getElementById('success-detail-service-row');
  const hint       = document.getElementById('success-confirmation-hint');
  const linkWrap   = document.getElementById('success-cancel-link-wrap');
  if (detailsBox) detailsBox.style.display = 'none';
  if (serviceRow) serviceRow.style.display = 'none';
  if (hint)       hint.style.display = 'none';
  if (linkWrap)   linkWrap.style.display = 'none';
  initCustomerForm();
}

// -----------------------------------------------------------------------------
// Service-Type-Toggle: zwischen Tisch-Reservierung und Abhol-Bestellung.
//
// Wichtig: Klick auf "Zum Mitnehmen" mutiert das Reservierungsformular
// NICHT. Das Formular bleibt unangetastet — wir öffnen stattdessen ein
// dediziertes Takeaway-Modal (siehe src/views/takeaway-form.js), das den
// kompletten Abhol-Bestellflow kapselt. Beim Schließen des Modals oder
// Klick auf "Zu Gast" verschwindet das Modal wieder, der Toggle springt
// zurück.
// -----------------------------------------------------------------------------
export function setServiceType(type) {
  if (type !== 'dine-in' && type !== 'takeaway' && type !== 'delivery') type = 'dine-in';
  // S266 — ein ausgeschalteter Kanal ist kein gültiges Ziel (Chip ist versteckt;
  // der Guard fängt Inline-/Console-Aufrufe). Fallback: erster aktiver Kanal.
  const settings = getState().settings || {};
  if (!guestChannelEnabled(settings, channelForServiceType(type))) {
    type = _defaultServiceType(settings);
    if (!guestChannelEnabled(settings, channelForServiceType(type))) return;
  }
  _serviceType = type;

  // Toggle-Chips aktualisieren — drei Optionen seit S61.
  const chipIn   = document.getElementById('svc-dine-in');
  const chipOut  = document.getElementById('svc-takeaway');
  const chipDel  = document.getElementById('svc-delivery');
  [
    [chipIn,  type === 'dine-in'],
    [chipOut, type === 'takeaway'],
    [chipDel, type === 'delivery']
  ].forEach(([el, active]) => {
    if (!el) return;
    el.classList.toggle('active', active);
    el.setAttribute('aria-checked', active ? 'true' : 'false');
  });

  // Modus-Wechsel: passenden Modal öffnen, anderen ggf. schließen.
  if (type === 'takeaway') {
    if (window.closeDeliveryModal) window.closeDeliveryModal({ silent: true });
    if (window.openTakeawayModal) window.openTakeawayModal();
  } else if (type === 'delivery') {
    if (window.closeTakeawayModal) window.closeTakeawayModal({ silent: true });
    if (window.openDeliveryModal) window.openDeliveryModal();
  } else {
    // dine-in
    if (window.closeTakeawayModal) window.closeTakeawayModal({ silent: true });
    if (window.closeDeliveryModal) window.closeDeliveryModal({ silent: true });
  }
}

/** Wird vom Takeaway-/Delivery-Modal aufgerufen, wenn der Gast schließt
 *  (X / Abbrechen / ESC). Setzt den Toggle zurück, ohne das Modal nochmal zu
 *  öffnen (sonst Schleife).
 *
 *  S266: Ist Reservieren AN, geht es zurück auf „Zu Gast" wie bisher. Ist
 *  Reservieren AUS (Bestell-Einstieg), führt Schließen zurück auf die
 *  Einstiegsseite — kein Chip aktiv, kein Sprung auf „Zu Gast", kein
 *  Auto-Öffnen. `_serviceType` bleibt auf dem ersten aktiven Kanal. */
export function _resetServiceTypeToggleSilently() {
  const settings = getState().settings || {};
  const layout = guestEntryLayout(settings);
  _serviceType = _defaultServiceType(settings);
  const chipIn   = document.getElementById('svc-dine-in');
  const chipOut  = document.getElementById('svc-takeaway');
  const chipDel  = document.getElementById('svc-delivery');
  const reservationMode = layout.mode === 'reservation';
  if (chipIn)  { chipIn.classList.toggle('active', reservationMode); chipIn.setAttribute('aria-checked', reservationMode ? 'true' : 'false'); }
  if (chipOut) { chipOut.classList.remove('active'); chipOut.setAttribute('aria-checked', 'false'); }
  if (chipDel) { chipDel.classList.remove('active'); chipDel.setAttribute('aria-checked', 'false'); }
}

// -----------------------------------------------------------------------------
// S266 — Gast-Kanäle: Einstiegslogik (pure) + DOM-Anwendung
// -----------------------------------------------------------------------------

/**
 * Pure — welchen Einstieg die Gastseite zeigt (Vitest deckt alle Kombinationen).
 *
 *   mode 'reservation' — Reservieren an: Reservierungsformular wie heute. Die
 *                        Chip-Leiste nur, wenn mindestens ein Bestell-Kanal an
 *                        ist (sonst entfällt sie). Hero „Tisch reservieren".
 *   mode 'order'       — Reservieren aus, Mitnehmen und/oder Lieferung an:
 *                        Formular versteckt, die Chips der aktiven Kanäle bleiben
 *                        als Einstieg (auch wenn es nur einer ist), kein Chip
 *                        vorausgewählt, kein Auto-Öffnen. Hero „Online bestellen".
 *   mode 'closed'      — alles aus: Hinweis-Karte statt Formular (Telefon).
 *
 * @returns {{mode:string, chips:string[], showChipBar:boolean, preorder:boolean,
 *            hero:string, defaultServiceType:string, activeChip:string|null}}
 */
export function guestEntryLayout(settings) {
  const s = settings || {};
  const active = activeGuestEntryChannels(s);
  const reservation = active.includes('reservation');
  const orderChannels = active.filter(k => k !== 'reservation');
  const chips = active.map(serviceTypeForChannel);
  let mode = 'closed';
  if (reservation) mode = 'reservation';
  else if (orderChannels.length > 0) mode = 'order';
  const showChipBar = mode === 'reservation' ? orderChannels.length > 0 : mode === 'order';
  return {
    mode,
    chips,
    chipCount: chips.length,
    showChipBar,
    preorder: reservation && guestChannelEnabled(s, 'reservationPreorder'),
    hero: mode === 'closed' ? 'Willkommen' : (mode === 'order' ? 'Online bestellen' : 'Tisch reservieren'),
    // S267 K3 — Kopfleisten-Label aus derselben Quelle wie der Hero.
    nav: mode === 'closed' ? 'Start'
       : (mode === 'order' ? 'Bestellen'
       : (orderChannels.length > 0 ? 'Reservieren & Bestellen' : 'Reservieren')),
    defaultServiceType: chips[0] || 'dine-in',
    activeChip: mode === 'reservation' ? 'dine-in' : null
  };
}

// -----------------------------------------------------------------------------
// S267 K1 — Flackern beim Laden: Pending-Marker bis zum ersten Apply, fail-open
// -----------------------------------------------------------------------------
// `#customer-form-wrapper[data-channels="pending"]` (index.html) hält Chips,
// Formular und Hero-Titel unsichtbar, bis `applyGuestChannels` zum ersten Mal
// gelaufen ist. Dauert der Settings-Load länger als `GUEST_CHANNELS_FAIL_OPEN_MS`
// oder scheitert er, fällt die Seite auf das Standard-Layout (alles an) zurück —
// der Server sperrt ohnehin, der Stale-Tab-Pfad fängt es freundlich ab.
export const GUEST_CHANNELS_FAIL_OPEN_MS = 3000;

/**
 * Pure — was die Gastseite zeigt (Vitest):
 *   'pending'  — noch kein Apply, Timeout nicht erreicht → Einstieg bleibt unsichtbar
 *   'ready'    — Apply ist gelaufen → Einstieg nach Kanälen
 *   'fallback' — kein Apply, aber Timeout erreicht (oder Load gescheitert)
 *                → Standard-Layout (alles an), fail-open
 */
export function guestChannelsGate({ applied = false, failed = false, elapsedMs = 0, timeoutMs = GUEST_CHANNELS_FAIL_OPEN_MS } = {}) {
  if (applied) return 'ready';
  if (failed || (Number(elapsedMs) || 0) >= timeoutMs) return 'fallback';
  return 'pending';
}

let _channelsApplied = false;
let _failOpenTimer = null;

function _clearChannelsPending() {
  const w = document.getElementById('customer-form-wrapper');
  if (w && w.getAttribute('data-channels') === 'pending') w.setAttribute('data-channels', 'ready');
}

/**
 * Fail-open-Wächter — vom Bootstrap VOR dem Settings-Load gestartet. Läuft der
 * Timer ab, bevor `applyGuestChannels` gelaufen ist, zeigt die Seite das
 * Standard-Layout (alles an). Kommt der Apply später doch, korrigiert er.
 */
export function armGuestChannelsFailOpen(timeoutMs = GUEST_CHANNELS_FAIL_OPEN_MS) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (_failOpenTimer) clearTimeout(_failOpenTimer);
  _failOpenTimer = setTimeout(() => {
    _failOpenTimer = null;
    if (guestChannelsGate({ applied: _channelsApplied, elapsedMs: timeoutMs, timeoutMs }) !== 'fallback') return;
    console.warn('[guest-channels] Settings nach ' + timeoutMs + ' ms nicht da — fail-open, Standard-Layout.');
    try { applyGuestChannels({}); } catch (e) { console.warn('[guest-channels] fail-open apply:', e); }
    _clearChannelsPending();
  }, timeoutMs);
}

/** Für Tests: Modul-Zustand zurücksetzen. */
export function _resetGuestChannelsGateForTests() {
  _channelsApplied = false;
  if (_failOpenTimer) clearTimeout(_failOpenTimer);
  _failOpenTimer = null;
}

/**
 * Wendet die Gast-Kanäle auf das DOM an — non-destruktiv (nur Attribute und
 * Sichtbarkeit; Formularwerte, Fokus und Scroll bleiben). Wird beim Init, nach
 * dem Reset und nach einer Server-Ablehnung (Settings neu geladen) gerufen.
 */
export function applyGuestChannels(settings) {
  const s = settings || getState().settings || {};
  const layout = guestEntryLayout(s);
  _serviceType = layout.defaultServiceType;

  // Chips: ausgeschaltete verstecken (hidden, nicht nur disabled).
  const chipFor = { 'dine-in': 'svc-dine-in', takeaway: 'svc-takeaway', delivery: 'svc-delivery' };
  Object.entries(chipFor).forEach(([type, id]) => {
    const el = document.getElementById(id);
    if (!el) return;
    const on = layout.chips.includes(type);
    el.hidden = !on;
    const active = on && layout.activeChip === type;
    el.classList.toggle('active', active);
    el.setAttribute('aria-checked', active ? 'true' : 'false');
  });
  const bar = document.querySelector('#reservation-form .service-toggle');
  if (bar) {
    bar.hidden = !layout.showChipBar;
    bar.classList.toggle('is-entry', layout.mode === 'order');
    bar.setAttribute('data-guest-mode', layout.mode);
    // S267 K2 — Raster folgt der Zahl sichtbarer Chips (1 = zentriert).
    bar.setAttribute('data-chip-count', String(layout.chipCount));
  }

  // Formular-Karte vs. Hinweis-Karte.
  const formCard = document.getElementById('customer-form-card');
  const closed   = document.getElementById('guest-closed-card');
  if (formCard) formCard.hidden = layout.mode === 'closed';
  if (closed) {
    closed.hidden = layout.mode !== 'closed';
    const phoneWrap = document.getElementById('guest-closed-phone');
    const phone = String(s.restaurantPhone || '').trim();
    if (phoneWrap) {
      phoneWrap.innerHTML = phone
        ? `Ruf uns gerne an: <a href="tel:${htmlEscape(phone.replace(/[^+\d]/g, ''))}">${htmlEscape(phone)}</a>`
        : '';
      phoneWrap.hidden = !phone;
    }
  }

  // Im Bestell-Einstieg ist das Reservierungsformular unsichtbar, die Chips
  // bleiben als Einstieg. Alle Formularteile außer der Chip-Leiste verstecken.
  const form = document.getElementById('reservation-form');
  if (form) {
    form.classList.toggle('is-order-entry', layout.mode === 'order');
    form.setAttribute('data-guest-mode', layout.mode);
  }

  // Vorbestellung: Checkbox + Sektion weg, wenn der Kanal aus ist.
  const preField = document.getElementById('r-preorder-field');
  const preToggle = document.getElementById('r-preorder-toggle');
  const preSection = document.getElementById('preorder-section');
  if (preField) preField.hidden = !layout.preorder;
  if (!layout.preorder) {
    if (preToggle) preToggle.checked = false;
    if (preSection) preSection.style.display = 'none';
  }

  // Hero.
  const heroTitle = document.getElementById('customer-hero-title');
  if (heroTitle) heroTitle.textContent = layout.hero;
  const heroSub = document.getElementById('customer-hero-sub');
  if (heroSub) heroSub.hidden = layout.mode === 'closed';

  // S267 K3 — Kopfleiste (#nav-customer) aus derselben Quelle wie der Hero.
  const navC = document.getElementById('nav-customer');
  if (navC && navC.textContent !== layout.nav) navC.textContent = layout.nav;

  // S267 K1 — erstes Apply: Pending-Marker weg, Fail-open-Wächter abbestellen.
  _channelsApplied = true;
  if (_failOpenTimer) { clearTimeout(_failOpenTimer); _failOpenTimer = null; }
  _clearChannelsPending();

  return layout;
}

/**
 * Settings frisch vom Server holen und den Einstieg neu bauen — nach einer
 * `CHANNEL_DISABLED`-Ablehnung (der Gast hatte einen veralteten Stand offen).
 * Schließt offene Bestell-Modals, damit der Gast wieder auf dem Einstieg steht.
 */
export async function refreshGuestChannels() {
  const st = window.App && window.App.storage;
  let settings = getState().settings || {};
  if (st && st.settings && typeof st.settings.reload === 'function') {
    try { settings = await st.settings.reload(); }
    catch (e) { console.warn('[refreshGuestChannels] settings.reload:', e); }
  }
  if (window.closeTakeawayModal) window.closeTakeawayModal({ silent: true });
  if (window.closeDeliveryModal) window.closeDeliveryModal({ silent: true });
  return applyGuestChannels(settings);
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    initCustomerForm,
    refreshAvailabilityPreview,
    renderAvailabilityPreview,
    applyAlternativeTime,
    togglePreorder,
    gatherPreorderItems,
    submitReservation,
    resetCustomerForm,
    setServiceType,
    getServiceType,
    _resetServiceTypeToggleSilently,
    // S266 — Gast-Kanäle (takeaway-form/delivery-form rufen refreshGuestChannels
    // über window, um keinen Import-Zyklus zu bauen).
    applyGuestChannels,
    refreshGuestChannels,
    guestEntryLabel,
    armGuestChannelsFailOpen
  });
}

// -----------------------------------------------------------------------------
// Pure-Helpers — Submit-Outcome + Race-Condition (S38 Test-Coverage)
// -----------------------------------------------------------------------------

/** Pure-Helper für Test-Coverage (S38): mappt evaluateReservation()-Output
 *  + Restaurant-Settings auf einen Outcome-Patch für `req` + UI-Feedback.
 *  4 Branches: waitlist (!ok + allowWaitlist + Kapazitäts-Reason), decline
 *  (!ok + sonst), manual (ok + requiresManual), direct (ok). Reine Branch-/
 *  String-Logic, kein DOM, kein Storage, kein Throw. Liefert alle Felder
 *  immer (assignedTables=[], hasAssignment=false in !ok-Branches). */
function _buildReservationSubmitOutcome(evalResult, settings) {
  const s = settings || {};
  let status = 'pending';
  let requiresManualConfirm = false;
  let assignedTables = [];
  let tablesCombined = false;
  let needsExtraChair = false;
  let extraChairsUsed = 0;
  let hasAssignment = false;
  let declineHint = null;
  let successMsg = '';
  let onWaitlist = false;

  if (!evalResult || !evalResult.ok) {
    // S132c — `waitlist` abgeschafft (phase_99): bei voller Kapazität läuft die
    // Anfrage nicht mehr auf die Warteliste, sondern als manuelle Anfrage
    // („wir melden uns persönlich") — der Operator entscheidet im Cockpit.
    const reason = (evalResult && evalResult.reason) || '';
    requiresManualConfirm = true;
    declineHint = reason;
    successMsg = 'Danke für deine Anfrage! Aktuell ist der gewünschte Zeitraum sehr gefragt. Wir melden uns persönlich bei dir, ob wir es einrichten können.';
  } else if (evalResult.requiresManual) {
    requiresManualConfirm = true;
    const a = evalResult.assignment || {};
    assignedTables = a.tables || [];
    tablesCombined = !!a.combined;
    needsExtraChair = !!a.needsChair;
    extraChairsUsed = a.chairsUsed || 0;
    hasAssignment = true;
    successMsg = `Danke für deine Anfrage! Bei Gruppen ab ${s.manualConfirmThreshold} Personen prüfen wir manuell — du bekommst kurzfristig eine Bestätigung per E-Mail.`;
  } else {
    const a = evalResult.assignment || {};
    assignedTables = a.tables || [];
    tablesCombined = !!a.combined;
    needsExtraChair = !!a.needsChair;
    extraChairsUsed = a.chairsUsed || 0;
    hasAssignment = true;
    successMsg = 'Wir haben deine Reservierung erhalten und melden uns gleich per E-Mail mit der finalen Bestätigung.';
  }

  return {
    status,
    requiresManualConfirm,
    assignedTables,
    tablesCombined,
    needsExtraChair,
    extraChairsUsed,
    hasAssignment,
    declineHint,
    successMsg,
    onWaitlist
  };
}

/** Pure-Helper für Test-Coverage (S38): liefert den Alternative-Slot-Text
 *  hinter dem GG001-Toast. Empty-Array/null/undefined → ''. */
function _buildGG001AlternativeText(alts) {
  if (!alts || !alts.length) return '';
  const slice = alts.slice(0, 3).map(a => (a && a.time) ? a.time : a).join(', ');
  return ' Verfügbare Alternativen: ' + slice + '.';
}

/** Pure-Helper für Test-Coverage (S38): zieht den `code`-String aus einem
 *  Storage-Error. Supabase-RPC liefert `err.code` direkt; bei Wrapped-Errors
 *  liegt er unter `err.details.code`. Liefert null, wenn nicht vorhanden. */
function _extractRaceErrorCode(err) {
  if (!err) return null;
  if (typeof err.code === 'string' && err.code) return err.code;
  if (err.details && typeof err.details.code === 'string' && err.details.code) return err.details.code;
  return null;
}

/** Pure-Helper für Test-Coverage (S38): Restaurant-ID-Resolver mit
 *  Fallback-Kette. Erst App.getCurrentRestaurantId, dann
 *  window.currentRestaurantId (function), dann AppConfig.defaultRestaurantId,
 *  dann 'rest_default'. */
function _currentRestaurantIdForTests(win) {
  const w = win || (typeof window !== 'undefined' ? window : {});
  if (w.App && typeof w.App.getCurrentRestaurantId === 'function') {
    return w.App.getCurrentRestaurantId();
  }
  if (typeof w.currentRestaurantId === 'function') return w.currentRestaurantId();
  return (w.AppConfig && w.AppConfig.defaultRestaurantId) || 'rest_default';
}

/** Module-State-Test-Helpers (S38): erlauben Tests, _serviceType direkt
 *  zu setzen und zurückzusetzen, ohne setServiceType (das hat DOM-Side-
 *  Effects). */
function _setServiceTypeForTests(type) {
  if (type !== 'dine-in' && type !== 'takeaway' && type !== 'delivery') type = 'dine-in';
  _serviceType = type;
}
function _resetServiceTypeForTests() {
  _serviceType = 'dine-in';
}

// -----------------------------------------------------------------------------
// Test-Hook-Exports (S38) — additiv, kein Laufzeit-Effekt
// -----------------------------------------------------------------------------
export {
  _buildDstHintText,
  _buildAvailabilityPreviewContent,
  _buildReservationSubmitOutcome,
  _buildGG001AlternativeText,
  _extractRaceErrorCode,
  _currentRestaurantIdForTests,
  _setServiceTypeForTests,
  _resetServiceTypeForTests
};
